import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
const DEFAULT_MAX_VISIBLE = 3;
const DEFAULT_GAP = 16;
const KEY_STEP = { ArrowLeft: -1, ArrowRight: 1 };
const Carousel = ({ items, children, label, maxVisible = DEFAULT_MAX_VISIBLE, gap = DEFAULT_GAP, showDots = true, className = '', }) => {
    const trackRef = React.useRef(null);
    const [page, setPage] = React.useState(0);
    const pageCount = Math.ceil(items.length / maxVisible);
    const isPaged = items.length > maxVisible;
    const hasPartialPage = isPaged && items.length % maxVisible > 0;
    // A trailing partial page is narrower than a full stride, so max scroll never
    // reaches its offset — treat "scrolled as far as it goes" as the last page.
    React.useEffect(() => {
        const track = trackRef.current;
        if (track) {
            const onScroll = () => {
                const remaining = track.scrollWidth - track.clientWidth - track.scrollLeft;
                setPage(track.scrollWidth > track.clientWidth && remaining < 1
                    ? pageCount - 1
                    : Math.round(track.scrollLeft / (track.clientWidth + gap)));
            };
            track.addEventListener('scroll', onScroll, { passive: true });
            return () => track.removeEventListener('scroll', onScroll);
        }
    }, [gap, pageCount]);
    const goToPage = (next) => {
        const track = trackRef.current;
        const clamped = Math.max(0, Math.min(next, pageCount - 1));
        if (track)
            track.scrollTo({ left: clamped * (track.clientWidth + gap) });
    };
    const onKeyDown = (event) => {
        const step = KEY_STEP[event.key];
        if (step) {
            event.preventDefault();
            goToPage(page + step);
        }
    };
    const itemBasis = `calc((100% - ${gap * (maxVisible - 1)}px) / ${maxVisible})`;
    return !items.length ? null : (_jsxs("div", { className: className, role: "region", "aria-roledescription": "carousel", "aria-label": label, children: [_jsxs("div", { className: "position-relative", children: [isPaged && (_jsx("button", { type: "button", className: "btn scroll-carousel-arrow scroll-carousel-arrow--prev", "aria-label": "Previous items", disabled: page === 0, onClick: () => goToPage(page - 1), children: _jsx("i", { className: "bi bi-chevron-left", "aria-hidden": "true" }) })), _jsx("div", { ref: trackRef, className: "d-flex scroll-carousel-track", style: { gap }, tabIndex: 0, onKeyDown: onKeyDown, children: items.map((item, index) => (_jsx("div", { className: "flex-shrink-0 scroll-carousel-item", style: { flexBasis: itemBasis }, children: children(item, index) }, index))) }), isPaged && (_jsx("button", { type: "button", className: "btn scroll-carousel-arrow scroll-carousel-arrow--next", "aria-label": "Next items", disabled: page >= pageCount - 1, onClick: () => goToPage(page + 1), children: _jsx("i", { className: "bi bi-chevron-right", "aria-hidden": "true" }) }))] }), isPaged && showDots && (_jsx("div", { className: "d-flex justify-content-center mt-2", children: Array.from({ length: pageCount }, (_, index) => (_jsx("button", { type: "button", className: [
                        'scroll-carousel-dot',
                        page === index ? 'active' : '',
                        hasPartialPage && index === pageCount - 1 ? 'scroll-carousel-dot--partial' : '',
                    ].filter(x => x.length > 0).join(' '), "aria-label": `Go to item group ${index + 1} of ${pageCount}`, "aria-current": page === index, onClick: () => goToPage(index) }, index))) }))] }));
};
export default Carousel;
